import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

export interface SiteSettings {
  id: string;
  site_name?: string;
  address_line_1?: string;
  address_line_2?: string;
  postal_code?: string;
  city?: string;
  phone?: string;
  email?: string;
  map_url?: string;
  facebook_url?: string;
  instagram_url?: string;
  linkedin_url?: string;
  preregistration_url?: string;
  ymag_preregistration_url?: string;
  inserjeunes_url?: string;
}

export interface DocumentResource {
  id: string;
  resource_key: string;
  title: string;
  category: string;
  resource_type: 'S3' | 'EXTERNAL';
  file?: string;
  external_url?: string;
  description?: string;
  year?: string;
  button_label?: string;
  display_order: number;
  visibility: boolean;
}

export interface EditorialContent {
  id: string;
  content_key: string;
  content_type: 'SECTION' | 'PARTNER' | 'TESTIMONIAL' | 'LEGAL';
  page: string;
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  body?: string;
  image?: string;
  image_alt?: string;
  button_label?: string;
  button_url?: string;
  display_order: number;
  visibility: boolean;
}

@Injectable({ providedIn: 'root' })
export class SiteContentService {
  private readonly headers = new HttpHeaders({
    'Content-Type': 'application/json',
    'x-api-key': environment.apiKey,
  });

  private readonly settingsRequest$: Observable<SiteSettings | null>;
  private readonly documentsRequest$: Observable<DocumentResource[]>;
  private readonly editorialContentsRequest$: Observable<EditorialContent[]>;

  constructor(private http: HttpClient) {
    this.settingsRequest$ = this.http.post<any>(environment.baseUrl, {
      query: `query PublicSiteSettings {
        getSiteSettings(id: "global") {
          id site_name address_line_1 address_line_2 postal_code city phone email map_url
          facebook_url instagram_url linkedin_url preregistration_url ymag_preregistration_url inserjeunes_url
        }
      }`,
    }, { headers: this.headers }).pipe(
      map((response) => response?.data?.getSiteSettings || null),
      shareReplay(1)
    );

    this.documentsRequest$ = this.http.post<any>(environment.baseUrl, {
      query: `query PublicDocumentResources {
        listDocumentResources(filter: { visibility: { eq: true } }, limit: 200) {
          items {
            id resource_key title category resource_type file external_url description year
            button_label display_order visibility
          }
        }
      }`,
    }, { headers: this.headers }).pipe(
      map((response) => (response?.data?.listDocumentResources?.items || []).filter(Boolean)),
      shareReplay(1)
    );

    this.editorialContentsRequest$ = this.http.post<any>(environment.baseUrl, {
      query: `query PublicEditorialContents {
        listEditorialContents(filter: { visibility: { eq: true } }, limit: 500) {
          items {
            id content_key content_type page eyebrow title subtitle body image image_alt
            button_label button_url display_order visibility
          }
        }
      }`,
    }, { headers: this.headers }).pipe(
      map((response) => (response?.data?.listEditorialContents?.items || [])
        .filter(Boolean)
        .sort((a, b) => a.display_order - b.display_order)),
      shareReplay(1)
    );
  }

  getSettings(): Observable<SiteSettings | null> {
    return this.settingsRequest$;
  }

  getDocuments(): Observable<DocumentResource[]> {
    return this.documentsRequest$;
  }

  getDocument(resourceKey: string): Observable<DocumentResource | null> {
    return this.documentsRequest$.pipe(
      map((documents) => documents.find((document) => document.resource_key === resourceKey) || null)
    );
  }

  getEditorialContents(page?: string, contentType?: EditorialContent['content_type']): Observable<EditorialContent[]> {
    return this.editorialContentsRequest$.pipe(
      map((contents) => contents.filter((content) =>
        (!page || content.page === page) && (!contentType || content.content_type === contentType)
      ))
    );
  }

  getEditorialContent(contentKey: string): Observable<EditorialContent | null> {
    return this.editorialContentsRequest$.pipe(
      map((contents) => contents.find((content) => content.content_key === contentKey) || null)
    );
  }

  resolveEditorialImage(content: EditorialContent | null | undefined, fallback = ''): string {
    if (!content?.image) return fallback;
    if (/^(https?:)?\/\//i.test(content.image) || content.image.startsWith('assets/')) return content.image;
    return environment.url + content.image;
  }

  resolveDocumentUrl(document: DocumentResource | null | undefined, fallback = ''): string {
    if (!document) return fallback;
    if (document.resource_type === 'EXTERNAL') return document.external_url || fallback;
    if (!document.file) return fallback;
    if (/^https?:\/\//i.test(document.file)) return document.file;
    return environment.url + document.file;
  }
}
